import ITabletopShapeRenderer from "../../objects/element/shape_renderer/ITabletopShapeRenderer";
import TabletopCanvasMouseEvent from "../events/TabletopCanvasMouseEvent";
import TabletopCanvasNavigationController from "./TabletopCanvasNavigationController";
import TabletopShapeElement from "../../objects/element/TabletopShapeElement";
import TabletopSimpleShapeRenderer from "../../objects/element/shape_renderer/TabletopSimpleShapeRenderer";
import TabletopUniverse from "../../objects/TabletopUniverse";
import TabletopViewport from "../../objects/TabletopViewport";

export default class TabletopCanvasDrawTerrainController extends TabletopCanvasNavigationController {
    private static defaultRenderer = new TabletopSimpleShapeRenderer({
        fillColor: "white",
        outlineStyle: "black",
        outlineWidth: 3,
    });
    private _workingShapeElement: TabletopShapeElement | null = null;
    private _shapeRenderer: ITabletopShapeRenderer =
        TabletopCanvasDrawTerrainController.defaultRenderer;

    public constructor(
        viewport: TabletopViewport,
        universe: TabletopUniverse,
        shapeRenderer?: ITabletopShapeRenderer,
    ) {
        super(viewport, universe);

        if (shapeRenderer != null) {
            this._shapeRenderer = shapeRenderer;
        }
    }

    public override dispose(): void {
        if (this._workingShapeElement != null) {
            this._universe.removeElement(this._workingShapeElement);
            this._workingShapeElement = null;
        }

        super.dispose();
    }

    public get shapeRenderer() {
        return this._shapeRenderer;
    }

    public set shapeRenderer(val: ITabletopShapeRenderer | null) {
        this._shapeRenderer =
            val ?? TabletopCanvasDrawTerrainController.defaultRenderer;

        if (this._workingShapeElement != null) {
            this._workingShapeElement.renderer =
                val ?? TabletopCanvasDrawTerrainController.defaultRenderer;
        }
    }

    public override onCanvasMouseMove(e: TabletopCanvasMouseEvent): void {
        super.onCanvasMouseMove(e);

        if (this._workingShapeElement != null) {
            const mouseUniversePosition =
                this._viewport.viewportPointToUniversePoint({
                    x: e.viewportPosition.x,
                    y: e.viewportPosition.y,
                });

            this._workingShapeElement.setLastVertex({
                x: mouseUniversePosition.x - this._workingShapeElement.x,
                y: mouseUniversePosition.y - this._workingShapeElement.y,
            });

            const p0 = this._workingShapeElement.vertexInViewport(
                0,
                this._viewport,
            );
            const pE = this._workingShapeElement.vertexInViewport(
                this._workingShapeElement.lastVertexIndex - 1,
                this._viewport,
            );

            if (p0 != null && pE != null) {
                const delta0 = Math.sqrt(
                    Math.pow(e.viewportPosition.x - p0.x, 2) +
                        Math.pow(e.viewportPosition.y - p0.y, 2),
                );
                const deltaE = Math.sqrt(
                    Math.pow(e.viewportPosition.x - pE.x, 2) +
                        Math.pow(e.viewportPosition.y - pE.y, 2),
                );

                this._workingShapeElement.highlightEdges =
                    delta0 <= 5 /* || deltaE <= 5 */;
            }
        }
    }

    public override onCanvasMouseDrag(e: TabletopCanvasMouseEvent): void {
        if (e.altKey) {
            super.onCanvasMouseDrag(e);
            return;
        }
    }

    public override onCanvasMouseDown(e: TabletopCanvasMouseEvent): void {
        if (this._workingShapeElement === null) {
            if (e.altKey) {
                return;
            }

            this._workingShapeElement = new TabletopShapeElement({
                x: e.universePosition.x,
                y: e.universePosition.y,
                vertices: [{ x: 0, y: 0 }],
                renderer: this._shapeRenderer,
            });

            this._universe.addElement(this._workingShapeElement);
        } else {
            if (e.altKey) {
                return;
            }

            const p0 = this._workingShapeElement.vertexInViewport(
                0,
                this._viewport,
            );
            const pE = this._workingShapeElement.vertexInViewport(
                this._workingShapeElement.lastVertexIndex - 1,
                this._viewport,
            );

            if (p0 != null && pE != null) {
                const delta0 = Math.sqrt(
                    Math.pow(e.viewportPosition.x - p0.x, 2) +
                        Math.pow(e.viewportPosition.y - p0.y, 2),
                );
                const deltaE = Math.sqrt(
                    Math.pow(e.viewportPosition.x - pE.x, 2) +
                        Math.pow(e.viewportPosition.y - pE.y, 2),
                );

                if (delta0 <= 5 || deltaE <= 5) {
                    this._workingShapeElement.highlightEdges = false;
                    this._workingShapeElement.removeVertexAt(
                        this._workingShapeElement.vertices.length - 1,
                    );
                    if (this._workingShapeElement.edgeCount >= 3) {
                        this._universe.addElement(
                            this._workingShapeElement.clone(),
                        );
                        this._universe.removeElement(this._workingShapeElement);
                    }
                    this._workingShapeElement = null;
                    return;
                }
            }
        }

        this._workingShapeElement.addVertex({
            x: e.universePosition.x - this._workingShapeElement.x,
            y: e.universePosition.y - this._workingShapeElement.y,
        });
    }
}
